/**
 * Centralized error handling utilities
 * Provides consistent error handling across the application
 */

export interface ApiErrorResponse {
  detail?: string;
  message?: string;
  error?: string;
  errors?: Record<string, string[]>;
}

/**
 * Custom API Error class with additional context
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public response?: ApiErrorResponse
  ) {
    super(message);
    this.name = 'ApiError';
    // Maintains proper stack trace for where error was thrown
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError);
    }
  }
}

/**
 * Handles API response and throws ApiError if response is not ok
 * @param response - Fetch API response
 * @returns Parsed JSON response
 * @throws ApiError if response is not ok
 */
export async function handleApiResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    let errorData: ApiErrorResponse | undefined;

    try {
      errorData = JSON.parse(errorText) as ApiErrorResponse;
      errorMessage = errorData.detail || errorData.message || errorData.error || errorMessage;
    } catch {
      // If JSON parsing fails, use the error text or default message
      errorMessage = errorText || errorMessage;
    }

    throw new ApiError(errorMessage, response.status, errorData);
  }

  return response.json();
}

/**
 * Type guard to check if error is an ApiError
 */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * Extracts error message from various error types
 * @param error - Unknown error object
 * @param fallback - Fallback message if error message cannot be extracted
 * @returns Error message string
 */
export function getErrorMessage(error: unknown, fallback = 'An error occurred'): string {
  // ApiError (our custom error)
  if (isApiError(error)) {
    return error.message;
  }

  // Standard Error
  if (error instanceof Error) {
    return error.message;
  }

  // Error with response object (from axios/fetch)
  if (typeof error === 'object' && error !== null) {
    const err = error as { response?: { data?: ApiErrorResponse }; message?: string };
    
    if (err.response?.data?.detail) {
      return err.response.data.detail;
    }
    if (err.response?.data?.message) {
      return err.response.data.message;
    }
    if (err.message) {
      return err.message;
    }
  }

  // String error
  if (typeof error === 'string') {
    return error;
  }

  return fallback;
}

/**
 * Logs error to console (and can be extended to log to error tracking service)
 * @param error - Error to log
 * @param context - Additional context about where error occurred
 */
export function logError(error: unknown, context?: string): void {
  const message = getErrorMessage(error);
  const prefix = context ? `[${context}]` : '';
  
  console.error(`${prefix} Error:`, message, error);
  
  // TODO: Add error tracking service integration (Sentry, etc.)
  // if (window.Sentry) {
  //   window.Sentry.captureException(error, { tags: { context } });
  // }
}

